import { useEffect, useState } from "react";
import { Card, Col, Row, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMapMarkerAlt, faChevronDown, faChevronUp, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import { ClientService } from "../../component/services/client.service";
import { makeParams } from "../../component/api/makeRequest";
import { DateRange } from "../../component/DateRangeSelector/DateRangeSelector";
import { useTheme } from "../../theme/useTheme";
import moment from "moment";

interface DistrictEntry {
    district: string;
    failureCount: number;
}

interface VendorDistrictStat {
    vendorName: string;
    totalFailures: number;
    districts: DistrictEntry[];
}

interface Props {
    dateRange: DateRange;
}

function FailureBar({ value, max }: { value: number; max: number }) {
    const pct = max ? Math.round((value / max) * 100) : 0;
    const color = pct >= 70 ? "#ef4444" : pct >= 40 ? "#f59e0b" : "#10b981";
    return (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div
                style={{
                    flex: 1,
                    height: 6,
                    borderRadius: 99,
                    background: "#f3f4f6",
                    overflow: "hidden",
                }}
            >
                <div
                    style={{
                        width: `${pct}%`,
                        height: "100%",
                        background: color,
                        borderRadius: 99,
                        transition: "width 0.4s ease",
                    }}
                />
            </div>
            <span style={{ fontSize: "0.75rem", fontWeight: 600, color, minWidth: 28, textAlign: "right" }}>
                {value}
            </span>
        </div>
    );
}

function VendorCard({ vendor }: { vendor: VendorDistrictStat }) {
    const { colors } = useTheme();
    const [expanded, setExpanded] = useState(true);
    const maxFailures = vendor.districts[0]?.failureCount ?? 1;
    const top5 = vendor.districts.slice(0, 5);
    const rest = vendor.districts.slice(5);
    const [showAll, setShowAll] = useState(false);
    const displayed = showAll ? vendor.districts : top5;

    return (
        <Card
            style={{
                borderRadius: "14px",
                border: `1px solid ${colors.border.light}`,
                boxShadow: colors.shadow,
                overflow: "hidden",
            }}
        >
            {/* Vendor header */}
            <div
                className="d-flex align-items-center justify-content-between px-4 py-3"
                style={{
                    background: colors.background.secondary,
                    borderBottom: expanded ? `1px solid ${colors.border.light}` : "none",
                    cursor: "pointer",
                    userSelect: "none",
                }}
                onClick={() => setExpanded((v) => !v)}
            >
                <div className="d-flex align-items-center gap-3">
                    <div
                        style={{
                            width: 36,
                            height: 36,
                            borderRadius: "9px",
                            background: "#fee2e2",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                        }}
                    >
                        <FontAwesomeIcon icon={faTriangleExclamation} style={{ color: "#ef4444", fontSize: "0.9rem" }} />
                    </div>
                    <div>
                        <div style={{ fontWeight: 700, fontSize: "0.95rem", color: colors.text.primary }}>
                            {vendor.vendorName}
                        </div>
                        <div style={{ fontSize: "0.72rem", color: colors.text.muted }}>
                            {vendor.districts.length} districts &middot; {vendor.totalFailures.toLocaleString()} total failures
                        </div>
                    </div>
                </div>
                <FontAwesomeIcon
                    icon={expanded ? faChevronUp : faChevronDown}
                    style={{ color: colors.text.muted, fontSize: "0.8rem" }}
                />
            </div>

            {/* District rows */}
            {expanded && (
                <div className="p-3">
                    {displayed.map((d, i) => (
                        <div
                            key={d.district}
                            className="d-flex align-items-center gap-3 py-2 px-1"
                            style={{
                                borderBottom: i < displayed.length - 1 ? `1px solid ${colors.border.light}` : "none",
                            }}
                        >
                            <div style={{ width: 22, textAlign: "center" }}>
                                <span style={{ fontSize: "0.7rem", color: colors.text.muted, fontWeight: 600 }}>
                                    {i + 1}
                                </span>
                            </div>
                            <FontAwesomeIcon
                                icon={faMapMarkerAlt}
                                style={{ color: colors.primary.main, fontSize: "0.75rem", flexShrink: 0 }}
                            />
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div
                                    style={{
                                        fontSize: "0.82rem",
                                        fontWeight: 500,
                                        color: colors.text.primary,
                                        marginBottom: 4,
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                    }}
                                >
                                    {d.district}
                                </div>
                                <FailureBar value={d.failureCount} max={maxFailures} />
                            </div>
                        </div>
                    ))}

                    {rest.length > 0 && (
                        <button
                            className="btn btn-link btn-sm p-0 mt-2"
                            style={{ color: colors.primary.main, fontSize: "0.78rem" }}
                            onClick={() => setShowAll((v) => !v)}
                        >
                            {showAll ? "Show less" : `+${rest.length} more districts`}
                        </button>
                    )}
                </div>
            )}
        </Card>
    );
}

export default function VendorDistrictFailures({ dateRange }: Props) {
    const { colors } = useTheme();
    const [data, setData] = useState<VendorDistrictStat[]>([]);
    const [loading, setLoading] = useState(false);

    async function fetchData(start: Date, end: Date) {
        setLoading(true);
        setData([]);
        const params = makeParams([
            { index: "start_time", value: moment(start).toISOString() },
            { index: "end_time", value: moment(end).toISOString() },
        ]);
        await ClientService.getVendorDistrictFailures(params)
            .then((res: any) => setData(res.data?.data || []))
            .catch((err: any) => console.log(err))
            .finally(() => setLoading(false));
    }

    useEffect(() => {
        fetchData(dateRange.startDate, dateRange.endDate);
    }, [dateRange.startDate, dateRange.endDate]);

    return (
        <div className="mt-4 mb-5">
            <div className="mb-3">
                <h6 style={{ fontWeight: 700, color: colors.text.primary, fontSize: "1rem", marginBottom: 2 }}>
                    District-wise Failures by Vendor
                </h6>
                <span style={{ color: colors.text.muted, fontSize: "0.78rem" }}>
                    Districts ranked by distinct students with no successful reverification attempt
                </span>
            </div>

            {loading ? (
                <div className="d-flex align-items-center justify-content-center py-5">
                    <Spinner animation="border" style={{ color: colors.primary.main }} />
                </div>
            ) : data.length === 0 ? (
                <div className="d-flex flex-column align-items-center justify-content-center py-5 text-center">
                    <FontAwesomeIcon
                        icon={faMapMarkerAlt}
                        style={{ fontSize: "2.5rem", color: colors.border.secondary, marginBottom: "1rem" }}
                    />
                    <span style={{ color: colors.text.muted, fontSize: "0.9rem" }}>
                        No failure data for the selected range
                    </span>
                </div>
            ) : (
                <Row className="g-3">
                    {data.map((vendor) => (
                        <Col key={vendor.vendorName} md={6} xl={4}>
                            <VendorCard vendor={vendor} />
                        </Col>
                    ))}
                </Row>
            )}
        </div>
    );
}
